import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { safeArray, safeObject } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Lock, Unlock, Info, Filter, RefreshCw, Activity, ShieldAlert, ArrowRightLeft, Tag, Search, X, XCircle, Hash, CheckCircle, Clock, ShieldOff, UserCog, History, Trash2 } from 'lucide-react';
import { StatCard } from '../components/StatCard';
import { SearchableSelect } from '../components/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function Linhas() {
  const { isAdmin } = useAuth();
  const [linhas, setLinhas] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [blockReasons, setBlockReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [blockPartialDialog, setBlockPartialDialog] = useState(false);
  const [blockTotalDialog, setBlockTotalDialog] = useState(false);
  const [unblockDialog, setUnblockDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [planChangeDialog, setPlanChangeDialog] = useState(false);

  const [selectedLinha, setSelectedLinha] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [selectedNewOferta, setSelectedNewOferta] = useState('');
  const [lineStatus, setLineStatus] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Numero historico (portabilidade)
  const [historicoDialog, setHistoricoDialog] = useState(false);
  const [novoNumeroHistorico, setNovoNumeroHistorico] = useState('');

  // Desbloqueio de confianca
  const [confiancaDialog, setConfiancaDialog] = useState(false);
  const [confiancaDias, setConfiancaDias] = useState(2);
  const [confiancaMotivo, setConfiancaMotivo] = useState('');

  const fetchLinhas = useCallback(async () => {
    try {
      const params = statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {};
      const [linhasRes, ofertasRes, reasonsRes] = await Promise.all([
        axios.get(`${API_URL}/api/linhas`, { params, withCredentials: true }),
        axios.get(`${API_URL}/api/ofertas?ativo=true`, { withCredentials: true }),
        axios.get(`${API_URL}/api/operadora/motivos-bloqueio`, { withCredentials: true }),
      ]);
      setLinhas(safeArray(linhasRes.data));
      setOfertas(safeArray(ofertasRes.data));
      setBlockReasons(safeArray(reasonsRes.data?.reasons));
    } catch (error) {
      toast.error('Erro ao carregar linhas');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchLinhas(); }, [fetchLinhas]);

  // Block Partial
  const handleBlockPartial = async () => {
    if (!selectedLinha) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/linhas/${selectedLinha.id}/bloquear-parcial`, {}, { withCredentials: true });
      if (r.data.success) { toast.success(r.data.message); setBlockPartialDialog(false); fetchLinhas(); }
      else toast.error(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao bloquear');
    } finally { setProcessing(false); }
  };

  // Block Total
  const handleBlockTotal = async () => {
    if (!selectedLinha || !selectedReason) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/linhas/${selectedLinha.id}/bloquear-total`, { reason: parseInt(selectedReason) }, { withCredentials: true });
      if (r.data.success) { toast.success(r.data.message); setBlockTotalDialog(false); fetchLinhas(); }
      else toast.error(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao bloquear');
    } finally { setProcessing(false); }
  };

  // Unblock
  const handleUnblock = async () => {
    if (!selectedLinha) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/linhas/${selectedLinha.id}/desbloquear`, {}, { withCredentials: true });
      if (r.data.success) { toast.success(r.data.message); setUnblockDialog(false); fetchLinhas(); }
      else toast.error(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao desbloquear');
    } finally { setProcessing(false); }
  };

  // Cancel
  const [cancelDialog, setCancelDialog] = useState(false);

  // Transferir titularidade
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferDestino, setTransferDestino] = useState('');
  const [transferMigrar, setTransferMigrar] = useState(false);
  const [transferInativar, setTransferInativar] = useState(false);
  const [clientesList, setClientesList] = useState([]);

  const loadClientesParaTransferencia = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/clientes`, { withCredentials: true });
      setClientesList(safeArray(r.data));
    } catch { setClientesList([]); }
  }, []);

  const handleTransferirTitularidade = async () => {
    if (!selectedLinha || !transferDestino) {
      toast.error('Selecione o cliente destino');
      return;
    }
    if (transferDestino === selectedLinha.cliente_id) {
      toast.error('A linha ja pertence a esse cliente');
      return;
    }
    const destino = clientesList.find(c => c.id === transferDestino);
    const confirmMsg = `CONFIRMAR TRANSFERENCIA:\n\n` +
      `Linha: ${selectedLinha.msisdn || selectedLinha.numero}\n\n` +
      `DE: ${selectedLinha.cliente_nome}\n` +
      `PARA: ${destino?.nome || '?'}\n\n` +
      (transferMigrar ? '✓ Cobrancas pendentes serao migradas\n' : '') +
      (transferInativar ? '✓ Cliente origem sera inativado se ficar sem linhas\n' : '') +
      `\nConfirmar?`;
    if (!window.confirm(confirmMsg)) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/linhas/transferir-titularidade`, {
        linha_id: selectedLinha.id,
        cliente_destino_id: transferDestino,
        migrar_cobrancas_pendentes: transferMigrar,
        inativar_origem_se_vazio: transferInativar,
      }, { withCredentials: true });
      const { cobrancas_migradas, origem_inativado } = r.data;
      let msg = 'Titularidade transferida com sucesso!';
      if (cobrancas_migradas) msg += ` ${cobrancas_migradas} cobranca(s) migrada(s).`;
      if (origem_inativado) msg += ' Cliente origem inativado.';
      toast.success(msg);
      setTransferDialog(false);
      setTransferDestino('');
      setTransferMigrar(false);
      setTransferInativar(false);
      fetchLinhas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao transferir');
    }
    setProcessing(false);
  };
  const handleCancel = async () => {
    if (!selectedLinha) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/linhas/${selectedLinha.id}/cancelar`, {}, { withCredentials: true });
      if (r.data.success) { toast.success('Linha cancelada com sucesso'); setCancelDialog(false); fetchLinhas(); }
      else toast.error(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao cancelar');
    } finally { setProcessing(false); }
  };

  // Check Status
  const handleCheckStatus = async (linha) => {
    setSelectedLinha(linha);
    setLineStatus(null);
    setStatusDialog(true);
    setCheckingStatus(true);
    try {
      const r = await axios.get(`${API_URL}/api/linhas/${linha.id}/consultar`, { withCredentials: true });
      setLineStatus(safeObject(r.data, {}));
    } catch (e) {
      toast.error('Erro ao consultar status');
      setStatusDialog(false);
    } finally { setCheckingStatus(false); }
  };

  // Change Plan
  const handleChangePlan = async () => {
    if (!selectedLinha || !selectedNewOferta) return;
    setProcessing(true);
    try {
      const r = await axios.post(`${API_URL}/api/linhas/${selectedLinha.id}/alterar-plano`, { oferta_id: selectedNewOferta }, { withCredentials: true });
      if (r.data.success) {
        toast.success(`Plano alterado para ${r.data.new_plan}`);
        setPlanChangeDialog(false);
        fetchLinhas();
      } else toast.error(r.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao alterar plano');
    } finally { setProcessing(false); }
  };

  // Numero historico (para permitir login do cliente com numero antigo apos portabilidade)
  const handleAdicionarHistorico = async () => {
    if (!selectedLinha || !novoNumeroHistorico.trim()) return;
    setProcessing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/linhas/${selectedLinha.id}/msisdn-historico`,
        { numero: novoNumeroHistorico.trim() },
        { withCredentials: true },
      );
      if (r.data.success) {
        toast.success('Numero adicionado ao historico. Cliente ja pode logar no portal.');
        setSelectedLinha({ ...selectedLinha, msisdn_historico: r.data.msisdn_historico });
        setNovoNumeroHistorico('');
        fetchLinhas();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao adicionar numero');
    } finally { setProcessing(false); }
  };

  const handleRemoverHistorico = async (numero) => {
    if (!selectedLinha) return;
    if (!window.confirm(`Remover ${numero} do historico?`)) return;
    try {
      const r = await axios.delete(
        `${API_URL}/api/linhas/${selectedLinha.id}/msisdn-historico/${numero}`,
        { withCredentials: true },
      );
      if (r.data.success) {
        toast.success('Numero removido');
        const novoHist = (selectedLinha.msisdn_historico || []).filter(n => n !== numero);
        setSelectedLinha({ ...selectedLinha, msisdn_historico: novoHist });
        fetchLinhas();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
  };

  const handleDesbloqueioConfianca = async () => {
    if (!selectedLinha) return;
    setProcessing(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/automacao/bloqueio/linhas/${selectedLinha.id}/desbloqueio-confianca`,
        { dias: confiancaDias, motivo: confiancaMotivo || null },
        { withCredentials: true },
      );
      if (r.data.success) {
        toast.success(`Desbloqueio de confiança concedido por ${confiancaDias} dias`);
        setConfiancaDialog(false);
        setConfiancaMotivo('');
        setConfiancaDias(2);
        fetchLinhas();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao desbloquear');
    }
    setProcessing(false);
  };

  const handleSyncExpiracaoTa = async () => {
    if (!window.confirm('Sincronizar a data de expiração do plano na Tip para TODAS as linhas ativas? Pode demorar 1-3 minutos.')) return;
    try {
      const r = await axios.post(`${API_URL}/api/automacao/bloqueio/sincronizar-expiracao-ta`, {}, { withCredentials: true });
      toast.success(`✅ Sync Tip: ${r.data.atualizadas} atualizadas, ${r.data.sem_expiracao} sem campo, ${r.data.erros?.length || 0} erros`);
      fetchLinhas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro na sincronizacao');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      ativo: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40',
      bloqueado: 'bg-red-500/15 text-red-400 border-red-500/40',
      pendente: 'bg-amber-500/15 text-amber-400 border-amber-500/40',
      erro: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    const icons = {
      ativo: <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />,
      bloqueado: <Lock className="w-3 h-3" />,
      pendente: <RefreshCw className="w-3 h-3 animate-spin" />,
      erro: <Activity className="w-3 h-3" />,
    };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-medium border ${styles[status] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'}`}>
        {icons[status]}{status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="linhas-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="page-title flex items-center gap-3"><Phone className="w-7 h-7 text-purple-500" />Linhas</h1>
          <p className="text-zinc-300 text-sm -mt-4">Gerenciamento de linhas ativas</p>
        </div>
        <Button onClick={fetchLinhas} variant="outline" className="btn-secondary flex items-center gap-2 w-full sm:w-auto" data-testid="refresh-linhas">
          <RefreshCw className="w-4 h-4" />Atualizar
        </Button>
        {isAdmin && (
          <Button onClick={handleSyncExpiracaoTa} variant="outline" className="border-emerald-700 text-emerald-300 hover:bg-emerald-900/20 flex items-center gap-2 w-full sm:w-auto" data-testid="btn-sync-expiracao-ta">
            <Activity className="w-4 h-4" /> Sync Expiração Tá
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-zinc-400" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48 form-input" data-testid="linha-status-filter"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-500/60">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ativo">Ativo</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="bloqueado">Bloqueado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar por nome, CPF, ICCID, numero..."
            className="form-input w-full pl-9 pr-9 py-2 text-sm"
            data-testid="linha-search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white" data-testid="linha-search-clear">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Stats */}
      {(() => {
        const q = searchTerm.toLowerCase().trim();
        const filteredLinhas = q
          ? linhas.filter(l =>
              (l.msisdn || '').toLowerCase().includes(q) ||
              (l.numero || '').toLowerCase().includes(q) ||
              (l.cliente_nome || '').toLowerCase().includes(q) ||
              (l.iccid || '').toLowerCase().includes(q) ||
              (l.cliente_documento || '').toLowerCase().includes(q)
            )
          : linhas;
        const displayLinhas = [...filteredLinhas].sort((a, b) =>
          (a.cliente_nome || '').localeCompare(b.cliente_nome || '', 'pt-BR', { sensitivity: 'base' })
        );
        return (
          <>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <StatCard icon={Hash} label="Total" value={displayLinhas.length} color="blue" testId="stat-total-linhas" />
        <StatCard icon={CheckCircle} label="Ativas" value={displayLinhas.filter(l => l.status === 'ativo').length} color="emerald" testId="stat-ativas" />
        <StatCard icon={Clock} label="Pendentes" value={displayLinhas.filter(l => l.status === 'pendente').length} color="amber" testId="stat-pendentes" />
        <StatCard icon={ShieldOff} label="Bloqueadas" value={displayLinhas.filter(l => l.status === 'bloqueado').length} color="red" testId="stat-bloqueadas" />
      </div>

      {/* Table */}
      <div className="dashboard-card overflow-hidden">
        <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-280px)]">
          <table className="data-table w-full min-w-[1400px]" data-testid="linhas-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-blue-950/80 backdrop-blur-sm border-b border-blue-800/50">
                <th className="text-blue-300 whitespace-nowrap min-w-[160px]">Numero</th>
                <th className="text-blue-300 whitespace-nowrap min-w-[200px]">Cliente</th>
                <th className="text-blue-300 whitespace-nowrap min-w-[200px]">ICCID</th>
                <th className="text-blue-300 whitespace-nowrap">Plano</th>
                <th className="text-blue-300 whitespace-nowrap">Oferta</th>
                <th className="text-blue-300 whitespace-nowrap">Status</th>
                <th className="text-blue-300 text-right whitespace-nowrap">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {displayLinhas.length === 0 ? (
                <tr><td colSpan={7} className="text-center text-zinc-500 py-8">Nenhuma linha encontrada</td></tr>
              ) : displayLinhas.map((linha) => (
                <tr key={linha.id} data-testid={`linha-row-${linha.id}`}>
                  <td className="font-mono text-white font-semibold text-sm whitespace-nowrap min-w-[160px]">{linha.msisdn || linha.numero}</td>
                  <td className="text-zinc-300 text-sm whitespace-nowrap min-w-[200px]">{linha.cliente_nome || '-'}</td>
                  <td className="font-mono text-zinc-300 text-sm whitespace-nowrap min-w-[200px]">{linha.iccid || '-'}</td>
                  <td className="text-zinc-300 text-sm whitespace-nowrap">{linha.plano_nome || '-'}</td>
                  <td className="text-zinc-300 text-sm whitespace-nowrap">{linha.oferta_nome || '-'}</td>
                  <td className="whitespace-nowrap">{getStatusBadge(linha.status)}</td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => handleCheckStatus(linha)} className="text-zinc-400 hover:text-blue-400" title="Consultar" data-testid={`check-status-${linha.id}`}>
                          <Info className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && linha.status === 'ativo' && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setBlockPartialDialog(true); }} className="text-zinc-400 hover:text-amber-400" title="Bloqueio Parcial" data-testid={`block-partial-${linha.id}`}>
                            <Lock className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setSelectedReason(''); setBlockTotalDialog(true); }} className="text-zinc-400 hover:text-red-400" title="Bloqueio Total" data-testid={`block-total-${linha.id}`}>
                            <ShieldAlert className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setSelectedNewOferta(''); setPlanChangeDialog(true); }} className="text-zinc-400 hover:text-blue-400" title="Alterar Plano" data-testid={`change-plan-${linha.id}`}>
                            <ArrowRightLeft className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {isAdmin && linha.status === 'bloqueado' && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setUnblockDialog(true); }} className="text-zinc-400 hover:text-emerald-400" title="Desbloquear" data-testid={`unblock-${linha.id}`}>
                          <Unlock className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && linha.status !== 'cancelado' && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setTransferDestino(''); setTransferMigrar(false); setTransferInativar(false); setTransferDialog(true); loadClientesParaTransferencia(); }} className="text-zinc-400 hover:text-violet-400" title="Transferir Titularidade" data-testid={`transfer-titularidade-${linha.id}`}>
                          <UserCog className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setNovoNumeroHistorico(''); setHistoricoDialog(true); }} className="text-zinc-400 hover:text-cyan-400" title="Numeros historicos (portabilidade)" data-testid={`historico-numero-${linha.id}`}>
                          <History className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && linha.status === 'bloqueado' && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setConfiancaDias(2); setConfiancaMotivo(''); setConfiancaDialog(true); }} className="text-zinc-400 hover:text-amber-400" title="Desbloqueio de Confianca" data-testid={`confianca-btn-${linha.id}`}>
                          <Unlock className="w-4 h-4" />
                        </Button>
                      )}
                      {isAdmin && linha.status !== 'cancelado' && (
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedLinha(linha); setCancelDialog(true); }} className="text-zinc-400 hover:text-red-500" title="Cancelar Linha" data-testid={`cancel-line-${linha.id}`}>
                          <XCircle className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
          </>
        );
      })()}

      {/* Block Partial Dialog */}
      <Dialog open={blockPartialDialog} onOpenChange={setBlockPartialDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-500/60">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Lock className="w-5 h-5 text-amber-400" />Bloqueio Parcial</DialogTitle></DialogHeader>
          <div className="py-2">
            <div className="p-3 bg-zinc-800/50 rounded-sm mb-3">
              <p className="text-xs text-zinc-300">Numero</p>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
              <p className="text-xs text-zinc-500 mt-1">Cliente: {selectedLinha?.cliente_nome}</p>
            </div>
            <p className="text-zinc-300 text-sm">Bloquear parcialmente esta linha? O usuario nao podera fazer chamadas mas ainda recebera.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockPartialDialog(false)} className="btn-secondary" disabled={processing}>Cancelar</Button>
            <Button onClick={handleBlockPartial} disabled={processing} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-block-partial">
              {processing ? 'Processando...' : 'Bloquear Parcial'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Total Dialog */}
      <Dialog open={blockTotalDialog} onOpenChange={setBlockTotalDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-500/60">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-red-400" />Bloqueio Total</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div className="p-3 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-300">Numero</p>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
              <p className="text-xs text-zinc-500 mt-1">Cliente: {selectedLinha?.cliente_nome}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-zinc-300">Motivo do bloqueio:</p>
              <Select value={selectedReason} onValueChange={setSelectedReason}>
                <SelectTrigger className="form-input" data-testid="block-reason-select"><SelectValue placeholder="Selecione o motivo" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-500/60">
                  {blockReasons.map(r => (
                    <SelectItem key={r.code} value={String(r.code)}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-red-400 text-xs">Bloqueio total suspende completamente a linha.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockTotalDialog(false)} className="btn-secondary" disabled={processing}>Cancelar</Button>
            <Button onClick={handleBlockTotal} disabled={processing || !selectedReason} className="btn-danger" data-testid="confirm-block-total">
              {processing ? 'Processando...' : 'Bloquear Total'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unblock Dialog */}
      <Dialog open={unblockDialog} onOpenChange={setUnblockDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-500/60">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Unlock className="w-5 h-5 text-emerald-400" />Desbloquear Linha</DialogTitle></DialogHeader>
          <div className="py-2">
            <div className="p-3 bg-zinc-800/50 rounded-sm mb-3">
              <p className="text-xs text-zinc-300">Numero</p>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
              <p className="text-xs text-zinc-500 mt-1">Cliente: {selectedLinha?.cliente_nome}</p>
            </div>
            <p className="text-zinc-300 text-sm">Desbloquear esta linha? A linha sera reativada imediatamente.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnblockDialog(false)} className="btn-secondary" disabled={processing}>Cancelar</Button>
            <Button onClick={handleUnblock} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="confirm-unblock">
              {processing ? 'Processando...' : 'Desbloquear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Change Dialog */}
      <Dialog open={planChangeDialog} onOpenChange={setPlanChangeDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-500/60">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><ArrowRightLeft className="w-5 h-5 text-blue-400" />Alterar Plano</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div className="p-3 bg-zinc-800/50 rounded-sm">
              <p className="text-xs text-zinc-300">Numero / Plano Atual</p>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
              <p className="text-sm text-zinc-400 mt-1">{selectedLinha?.plano_nome} - {selectedLinha?.oferta_nome}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-zinc-300">Nova Oferta / Plano:</p>
              <Select value={selectedNewOferta} onValueChange={setSelectedNewOferta}>
                <SelectTrigger className="form-input" data-testid="new-oferta-select"><SelectValue placeholder="Selecione a nova oferta" /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                  {ofertas.filter(o => o.id !== selectedLinha?.oferta_id).map(o => (
                    <SelectItem key={o.id} value={o.id}>
                      <span className="flex items-center gap-2">
                        <Tag className="w-3 h-3 text-blue-400" />
                        {o.nome} - R$ {o.valor?.toFixed(2)} ({o.franquia})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanChangeDialog(false)} className="btn-secondary" disabled={processing}>Cancelar</Button>
            <Button onClick={handleChangePlan} disabled={processing || !selectedNewOferta} className="btn-primary" data-testid="confirm-plan-change">
              {processing ? 'Processando...' : 'Alterar Plano'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-500/60">
          <DialogHeader><DialogTitle className="text-white flex items-center gap-2"><Activity className="w-5 h-5 text-blue-400" />Consulta da Linha</DialogTitle></DialogHeader>
          <div className="py-2">
            {checkingStatus ? (
              <div className="flex flex-col items-center py-8 gap-4">
                <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-zinc-400">Consultando operadora...</p>
              </div>
            ) : lineStatus ? (
              <div className="space-y-3">
                <div className={`p-3 rounded-sm ${lineStatus.success ? 'bg-emerald-500/15 border border-emerald-500/40' : 'bg-red-500/15 border border-red-500/40'}`}>
                  <p className="text-xs text-zinc-500 mb-1">Status</p>
                  <p className={`text-sm font-semibold ${lineStatus.success ? 'text-emerald-400' : 'text-red-400'}`}>
                    {lineStatus.success ? 'Consulta realizada' : 'Erro na consulta'}
                  </p>
                  <p className="text-xs text-zinc-400 mt-1">{lineStatus.message}</p>
                </div>
                {lineStatus.data && (
                  <div className="space-y-2 text-sm">
                    {Object.entries(lineStatus.data).filter(([k]) => !['raw'].includes(k)).map(([key, val]) => (
                      <div key={key} className="flex justify-between p-2 bg-zinc-800/50 rounded-sm">
                        <span className="text-zinc-500">{key}</span>
                        <span className="text-white font-mono text-xs">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {lineStatus.response_time_ms && (
                  <p className="text-xs text-zinc-600 text-center">Tempo: {lineStatus.response_time_ms}ms</p>
                )}
              </div>
            ) : (
              <p className="text-center text-zinc-500 py-8">Erro ao carregar dados</p>
            )}
          </div>
          <DialogFooter><Button onClick={() => setStatusDialog(false)} className="btn-secondary">Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialog} onOpenChange={setCancelDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-500/60">
          <DialogHeader><DialogTitle className="text-red-400">Cancelar Linha</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-zinc-300 text-sm">
              Tem certeza que deseja <strong className="text-red-400">cancelar</strong> a linha <strong className="text-white">{selectedLinha?.numero || selectedLinha?.msisdn}</strong>?
            </p>
            <p className="text-amber-400 text-xs">
              Esta ação é irreversível na operadora. A linha será desativada permanentemente.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)} className="border-zinc-700">Voltar</Button>
            <Button onClick={handleCancel} disabled={processing} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-cancel-line">
              {processing ? 'Cancelando...' : 'Confirmar Cancelamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transferir Titularidade */}
      <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
        <DialogContent className="bg-zinc-900 border-violet-500/40 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCog className="w-5 h-5 text-violet-400" />Transferir Titularidade da Linha
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="p-3 bg-zinc-800/60 rounded">
              <p className="text-xs text-zinc-400">Linha</p>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
              <p className="text-xs text-zinc-400 mt-2">Titular ATUAL</p>
              <p className="text-amber-300 text-sm font-semibold">{selectedLinha?.cliente_nome || '-'}</p>
            </div>
            <div>
              <label className="text-sm text-zinc-300 block mb-1">Novo titular (busque pelo nome COMPLETO ou CPF)</label>
              <SearchableSelect
                value={transferDestino}
                onValueChange={setTransferDestino}
                options={clientesList
                  .filter(c => c.id !== selectedLinha?.cliente_id)
                  .map(c => ({
                    value: c.id,
                    label: `${c.nome}${c.documento ? ' - ' + c.documento : ''}`,
                  }))}
                placeholder="Buscar cliente..."
                searchPlaceholder="Digite nome completo ou CPF..."
                testId="transfer-cliente-destino"
              />
              <p className="text-xs text-zinc-500 mt-1">⚠️ Atencao: busque pelo NOME COMPLETO para evitar confundir clientes com nomes parecidos.</p>
            </div>
            <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={transferMigrar} onChange={e => setTransferMigrar(e.target.checked)} className="mt-1" data-testid="transfer-migrar-cobrancas" />
              <span>Migrar cobrancas <strong>pendentes</strong> desta linha (somente NAO pagas)</span>
            </label>
            <label className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={transferInativar} onChange={e => setTransferInativar(e.target.checked)} className="mt-1" data-testid="transfer-inativar-origem" />
              <span>Inativar cliente origem se ficar sem linhas</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialog(false)} className="border-zinc-700" disabled={processing}>Cancelar</Button>
            <Button onClick={handleTransferirTitularidade} disabled={processing || !transferDestino} className="bg-violet-600 hover:bg-violet-500 text-white" data-testid="confirm-transfer-titularidade">
              {processing ? 'Transferindo...' : 'Transferir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Numeros historicos (portabilidade) */}
      <Dialog open={historicoDialog} onOpenChange={setHistoricoDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="historico-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-400" />Numeros Historicos (Portabilidade)
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Cadastre numeros antigos para permitir que o cliente logue no Portal com o numero anterior a portabilidade.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-zinc-900 rounded-lg p-3">
              <div className="text-xs text-zinc-500 mb-1">Numero atual</div>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
            </div>

            <div className="text-xs text-zinc-400 leading-relaxed bg-cyan-950/30 border border-cyan-800/40 rounded p-2">
              Adicione aqui numeros antigos (ex: numero Ta Telecom original antes da portabilidade)
              que o cliente pode usar para logar no Portal do Cliente.
            </div>

            <div>
              <label className="text-xs text-zinc-400 block mb-1">Numeros salvos</label>
              {(selectedLinha?.msisdn_historico || []).length === 0 ? (
                <div className="text-xs text-zinc-500 italic p-2 bg-zinc-900 rounded">Nenhum numero historico salvo</div>
              ) : (
                <div className="space-y-1">
                  {(selectedLinha?.msisdn_historico || []).map((n) => (
                    <div key={n} className="flex items-center justify-between bg-zinc-900 rounded px-3 py-2">
                      <span className="font-mono text-sm text-white">{n}</span>
                      <Button variant="ghost" size="sm" onClick={() => handleRemoverHistorico(n)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 w-7 p-0"
                        data-testid={`remove-historico-${n}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-zinc-400 block mb-1">Adicionar numero</label>
              <div className="flex gap-2">
                <input type="text"
                  value={novoNumeroHistorico}
                  onChange={e => setNovoNumeroHistorico(e.target.value)}
                  placeholder="Ex: 11987654321"
                  className="flex-1 bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-white font-mono"
                  data-testid="input-novo-historico"
                />
                <Button onClick={handleAdicionarHistorico}
                  disabled={processing || !novoNumeroHistorico.trim()}
                  className="btn-primary"
                  data-testid="btn-add-historico">
                  Adicionar
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoricoDialog(false)} className="border-zinc-700 hover:bg-zinc-800" data-testid="btn-close-historico">
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Desbloqueio de Confianca */}
      <Dialog open={confiancaDialog} onOpenChange={setConfiancaDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md" data-testid="confianca-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Unlock className="w-5 h-5 text-amber-400" /> Desbloqueio de Confiança
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Desbloqueia temporariamente. Se o cliente não pagar até o prazo, o sistema re-bloqueia automaticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-zinc-900 rounded p-3">
              <div className="text-xs text-zinc-500">Linha</div>
              <p className="text-white font-mono">{selectedLinha?.msisdn || selectedLinha?.numero}</p>
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Prazo (dias)</label>
              <input type="number" min="1" max="30" value={confiancaDias}
                onChange={e => setConfiancaDias(parseInt(e.target.value) || 2)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                data-testid="input-confianca-dias" />
            </div>
            <div>
              <label className="text-xs text-zinc-400 block mb-1">Motivo (opcional)</label>
              <input type="text" value={confiancaMotivo}
                onChange={e => setConfiancaMotivo(e.target.value)}
                placeholder="Ex: cliente enviou comprovante final de semana"
                className="w-full bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm"
                data-testid="input-confianca-motivo" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfiancaDialog(false)} className="border-zinc-700" disabled={processing}>Cancelar</Button>
            <Button onClick={handleDesbloqueioConfianca} disabled={processing} className="btn-primary" data-testid="btn-confirmar-confianca">
              {processing ? 'Desbloqueando...' : `Desbloquear por ${confiancaDias} dia${confiancaDias === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
